<template>
  <div class="tab-shell">
    <!-- ════════════ LISTA ════════════ -->
    <template v-if="view === 'list'">
      <div class="tab-head">
        <div>
          <h2 class="tab-title">Questionários</h2>
          <p class="tab-sub">Provas de múltipla escolha para treinamento e avaliação.</p>
        </div>
        <button v-if="!showCreate" class="btn-primary" type="button" @click="openCreate">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Novo questionário
        </button>
      </div>

      <div v-if="showCreate" class="form-card">
        <h3 class="form-title">Novo questionário</h3>
        <div class="form-grid form-grid--single">
          <label class="field">
            <span class="field-label">Título *</span>
            <input ref="createTitleRef" class="field-input" v-model="createForm.title" maxlength="200" placeholder="Ex: NR-35 — Trabalho em altura" @keyup.enter="create" />
          </label>
          <label class="field">
            <span class="field-label">Descrição</span>
            <textarea class="field-input field-textarea" v-model="createForm.description" rows="2" placeholder="Instruções exibidas antes de começar a prova"></textarea>
          </label>
        </div>
        <p v-if="createError" class="form-error">{{ createError }}</p>
        <div class="form-actions">
          <button class="btn-save" type="button" :disabled="creating" @click="create">{{ creating ? 'Criando...' : 'Criar e adicionar perguntas' }}</button>
          <button class="btn-cancel" type="button" @click="showCreate = false">Cancelar</button>
        </div>
      </div>

      <div v-if="loading" class="empty-hint">Carregando...</div>
      <div v-else-if="quizzes.length === 0 && !showCreate" class="empty-hint">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        Nenhum questionário criado.
      </div>
      <div v-else class="cards-grid">
        <article v-for="q in quizzes" :key="q.id" class="quiz-card">
          <div v-if="q.visibility === 'public' && q.publicHash" class="card-link-bar">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            <span class="card-link-label">Link público ativo</span>
            <button class="copy-link-btn" type="button" @click.stop="copyLink(q.publicHash)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              {{ copiedHash === q.publicHash ? 'Copiado!' : 'Copiar link' }}
            </button>
          </div>
          <div class="card-body" @click="openQuiz(q.id)">
            <div class="card-title-row">
              <div class="card-title">{{ q.title }}</div>
              <span class="vis-chip" :class="q.visibility === 'public' ? 'vis-chip--public' : 'vis-chip--private'">
                {{ q.visibility === 'public' ? 'Público' : 'Fechado' }}
              </span>
            </div>
            <div v-if="q.description" class="card-desc">{{ q.description }}</div>
            <div class="card-stats">
              <span>{{ q.totalQuestions }} pergunta{{ q.totalQuestions === 1 ? '' : 's' }}</span>
              <span v-if="q.categoriesCount > 1">· {{ q.categoriesCount }} categorias</span>
              <span>· {{ q.attemptsCount }} tentativa{{ q.attemptsCount === 1 ? '' : 's' }}</span>
              <span v-if="q.avgScore !== null">· média {{ formatPct(q.avgScore) }}</span>
            </div>
          </div>
          <div class="card-footer">
            <button class="card-btn" type="button" @click="openQuiz(q.id)">Editar</button>
            <button class="card-btn" type="button" @click="openQuiz(q.id, 'resultados')">Resultados</button>
            <button class="card-btn" type="button" :disabled="q.totalQuestions === 0" @click="startTake(q.id)">Responder</button>
            <button class="card-btn card-btn--warn" type="button" @click="removeQuiz(q)">Excluir</button>
          </div>
        </article>
      </div>
    </template>

    <!-- ════════════ DETALHE ════════════ -->
    <template v-else-if="view === 'detail'">
      <button class="back-link" type="button" @click="backToList">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="15 18 9 12 15 6"/></svg>
        Questionários
      </button>

      <div v-if="detailLoading" class="empty-hint">Carregando...</div>
      <template v-else-if="detail">
        <div class="tab-head">
          <div class="detail-title-wrap">
            <h2 class="tab-title">{{ editForm.title || 'Sem título' }}</h2>
            <span class="vis-chip" :class="detail.visibility === 'public' ? 'vis-chip--public' : 'vis-chip--private'">
              {{ detail.visibility === 'public' ? 'Público' : 'Fechado à organização' }}
            </span>
          </div>
          <div class="head-actions">
            <button v-if="detail.visibility === 'public' && detail.publicHash" class="btn-secondary" type="button" @click="copyLink(detail.publicHash)">
              {{ copiedHash === detail.publicHash ? 'Copiado!' : 'Copiar link' }}
            </button>
            <button class="btn-secondary" type="button" :disabled="totalQuestions === 0" @click="startTake(detail.id)">Responder</button>
          </div>
        </div>

        <div class="detail-layout" :class="{ 'detail-layout--wide': detailTab === 'resultados' }">
        <div class="detail-main">
        <div class="sub-tabs">
          <button class="sub-tab" :class="{ active: detailTab === 'perguntas' }" type="button" @click="detailTab = 'perguntas'">
            Perguntas <span class="sub-tab-count">{{ totalQuestions }}</span>
          </button>
          <button class="sub-tab" :class="{ active: detailTab === 'config' }" type="button" @click="detailTab = 'config'">Configurações</button>
          <button class="sub-tab" :class="{ active: detailTab === 'resultados' }" type="button" @click="openResults">
            Resultados <span class="sub-tab-count">{{ attempts.length || '' }}</span>
          </button>
        </div>

        <!-- ── Perguntas (por categoria) ── -->
        <div v-if="detailTab === 'perguntas'" class="pane">
          <div class="form-card">
            <div class="form-grid form-grid--single">
              <label class="field">
                <span class="field-label">Título *</span>
                <input class="field-input" v-model="editForm.title" maxlength="200" />
              </label>
              <label class="field">
                <span class="field-label">Descrição</span>
                <textarea class="field-input field-textarea" v-model="editForm.description" rows="2" placeholder="Instruções exibidas antes de começar a prova"></textarea>
              </label>
            </div>
          </div>

          <div class="cats-toolbar">
            <div class="cats-toolbar-text">
              <strong>Categorias</strong>
              <span>Quem responde passa por uma categoria de cada vez, na ordem abaixo.</span>
            </div>
            <div class="cats-toolbar-actions">
              <button class="btn-link" type="button" @click="setAllCollapsed(false)">Expandir todas</button>
              <button class="btn-link" type="button" @click="setAllCollapsed(true)">Recolher todas</button>
              <button class="btn-link" type="button" title="Planilha de exemplo para importar perguntas" @click="downloadTemplate">Modelo Excel</button>
            </div>
          </div>
          <input ref="importInputRef" type="file" accept=".xlsx,.xls,.csv" hidden @change="onImportFile" />

          <section
            v-for="(cat, ci) in editForm.categories"
            :key="cat.key"
            :id="'qcat-' + cat.key"
            class="cat-block"
            :class="{ 'cat-block--collapsed': cat.collapsed }"
          >
            <header class="cat-head">
              <button class="cat-toggle" type="button" :title="cat.collapsed ? 'Expandir' : 'Recolher'" @click="cat.collapsed = !cat.collapsed">
                <svg class="cat-chevron" :class="{ open: !cat.collapsed }" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <span class="cat-index">{{ ci + 1 }}</span>
              <input class="cat-name-input" v-model="cat.name" maxlength="120" placeholder="Nome da categoria" />
              <span class="cat-chip">{{ cat.questions.length }} pergunta{{ cat.questions.length === 1 ? '' : 's' }}</span>
              <span v-if="cat.mode !== 'all' && cat.questionCount > 0 && cat.questionCount < cat.questions.length" class="cat-chip cat-chip--accent">{{ cat.mode === 'first' ? `${cat.questionCount} primeiras` : `sorteia ${cat.questionCount}` }}</span>
              <span v-if="pendingInCategory(cat)" class="cat-chip cat-chip--warn">{{ pendingInCategory(cat) }} pendente{{ pendingInCategory(cat) === 1 ? '' : 's' }}</span>
              <div class="cat-tools">
                <button class="cat-import-btn" type="button" title="Importar perguntas do Excel para esta categoria" @click="pickImportFile(cat.key)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Importar
                </button>
                <button class="icon-btn" type="button" title="Mover categoria para cima" :disabled="ci === 0" @click="moveCategory(ci, -1)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="18 15 12 9 6 15"/></svg>
                </button>
                <button class="icon-btn" type="button" title="Mover categoria para baixo" :disabled="ci === editForm.categories.length - 1" @click="moveCategory(ci, 1)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <button class="icon-btn icon-btn--warn" type="button" title="Excluir categoria" :disabled="editForm.categories.length <= 1" @click="removeCategory(ci)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                </button>
              </div>
            </header>

            <div v-show="!cat.collapsed" class="cat-body">
              <div class="cat-settings">
                <span class="cat-settings-label">Por tentativa:</span>
                <select class="field-input mode-select" v-model="cat.mode">
                  <option value="all">Todas as perguntas</option>
                  <option value="random">Sortear uma quantidade</option>
                  <option value="first">As primeiras (na ordem)</option>
                </select>
                <template v-if="cat.mode !== 'all'">
                  <input class="field-input count-input" type="number" min="1" :max="cat.questions.length || undefined" v-model.number="cat.questionCount" />
                  <span class="field-hint cat-settings-hint">de {{ cat.questions.length }}</span>
                </template>
                <span class="cat-settings-result">→ {{ effectiveCount(cat) }} aparece{{ effectiveCount(cat) === 1 ? '' : 'm' }} por tentativa</span>
              </div>

              <div v-if="importPreview && importPreview.categoryKey === cat.key" class="form-card import-preview">
                <h3 class="form-title">Importar "{{ importPreview.fileName }}" em {{ cat.name || 'esta categoria' }}</h3>
                <p class="import-summary">
                  <strong>{{ importPreview.questions.length }}</strong> pergunta{{ importPreview.questions.length === 1 ? '' : 's' }} válida{{ importPreview.questions.length === 1 ? '' : 's' }}
                  <template v-if="importPreview.errors.length"> · <span class="import-err-count">{{ importPreview.errors.length }} linha{{ importPreview.errors.length === 1 ? '' : 's' }} com erro (serão ignoradas)</span></template>
                  <template v-if="importPreview.skippedHeader"> · linha 1 tratada como cabeçalho</template>
                </p>
                <ul v-if="importPreview.errors.length" class="import-errors">
                  <li v-for="err in importPreview.errors.slice(0, 8)" :key="err.row">Linha {{ err.row }}: {{ err.message }}</li>
                  <li v-if="importPreview.errors.length > 8">… e mais {{ importPreview.errors.length - 8 }}</li>
                </ul>
                <ol v-if="importPreview.questions.length" class="import-sample">
                  <li v-for="(q, i) in importPreview.questions.slice(0, 3)" :key="i">
                    {{ q.text }} <span class="import-sample-answer">→ {{ letter(q.correctIndex) }}) {{ q.options[q.correctIndex] }}</span>
                  </li>
                  <li v-if="importPreview.questions.length > 3" class="import-sample-more">… e mais {{ importPreview.questions.length - 3 }}</li>
                </ol>
                <div v-if="filledQuestions(cat).length && importPreview.questions.length" class="import-mode">
                  <label class="check-row"><input type="radio" value="append" v-model="importMode" /> <span>Adicionar às {{ filledQuestions(cat).length }} perguntas desta categoria</span></label>
                  <label class="check-row"><input type="radio" value="replace" v-model="importMode" /> <span>Substituir as perguntas desta categoria</span></label>
                </div>
                <div class="form-actions">
                  <button class="btn-save" type="button" :disabled="!importPreview.questions.length" @click="applyImport">
                    Importar {{ importPreview.questions.length }} pergunta{{ importPreview.questions.length === 1 ? '' : 's' }}
                  </button>
                  <button class="btn-cancel" type="button" @click="importPreview = null">Cancelar</button>
                </div>
              </div>

              <div v-if="cat.questions.length === 0" class="empty-hint empty-hint--compact">
                Nenhuma pergunta nesta categoria. Adicione abaixo ou use "Importar".
              </div>

              <article
                v-for="(q, qi) in cat.questions"
                :key="q.key"
                :id="'qe-' + q.key"
                class="q-editor"
                :class="{ 'q-editor--flash': flashKey === q.key }"
              >
                <div class="q-editor-head">
                  <span class="q-num">{{ qi + 1 }}</span>
                  <textarea class="field-input field-textarea q-text" v-model="q.text" rows="2" placeholder="Enunciado da pergunta"></textarea>
                  <div class="q-tools">
                    <button class="icon-btn" type="button" title="Mover para cima" :disabled="qi === 0" @click="moveQuestion(cat, qi, -1)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="18 15 12 9 6 15"/></svg>
                    </button>
                    <button class="icon-btn" type="button" title="Mover para baixo" :disabled="qi === cat.questions.length - 1" @click="moveQuestion(cat, qi, 1)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    <button class="icon-btn" type="button" title="Duplicar" @click="duplicateQuestion(cat, qi)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    </button>
                    <button class="icon-btn icon-btn--warn" type="button" title="Remover pergunta" @click="removeQuestion(cat, qi)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    </button>
                  </div>
                </div>
                <div class="opt-list">
                  <div v-for="(_opt, oi) in q.options" :key="oi" class="opt-row" :class="{ 'opt-row--correct': q.correctIndex === oi }">
                    <label class="opt-correct" :title="q.correctIndex === oi ? 'Alternativa correta' : 'Marcar como correta'">
                      <input type="radio" :name="'correct-' + q.key" :checked="q.correctIndex === oi" @change="q.correctIndex = oi" />
                      <span class="opt-letter">{{ letter(oi) }}</span>
                    </label>
                    <input class="field-input opt-input" v-model="q.options[oi]" :placeholder="'Alternativa ' + letter(oi)" />
                    <span v-if="q.correctIndex === oi" class="opt-correct-tag">Correta</span>
                    <button class="icon-btn icon-btn--warn" type="button" title="Remover alternativa" :disabled="q.options.length <= 2" @click="removeOption(q, oi)">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <button v-if="q.options.length < 10" class="add-opt-btn" type="button" @click="q.options.push('')">+ Alternativa</button>
                </div>
              </article>

              <button class="add-q-btn add-q-btn--small" type="button" @click="addQuestion(cat)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Adicionar pergunta em {{ cat.name || 'esta categoria' }}
              </button>
            </div>
          </section>

          <button class="add-q-btn" type="button" @click="addCategory">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nova categoria
          </button>

          <div class="save-bar">
            <div class="save-status">
              <span class="status-dot" :class="'status-dot--' + saveState"></span>
              <span class="save-status-text" :class="{ 'save-status-text--err': saveState === 'error' }">{{ saveStatusText }}</span>
            </div>
            <button class="btn-save" type="button" :disabled="saving" @click="saveQuestions()">{{ saving ? 'Salvando...' : 'Salvar perguntas' }}</button>
          </div>
        </div>

        <!-- ── Configurações ── -->
        <div v-else-if="detailTab === 'config'" class="pane">
          <section class="form-card">
            <h3 class="form-title">Acesso</h3>
            <div class="vis-options">
              <label class="vis-option" :class="{ active: settings.visibility === 'private' }">
                <input type="radio" value="private" v-model="settings.visibility" />
                <div>
                  <div class="vis-option-title">Fechado à organização</div>
                  <div class="vis-option-desc">Só usuários logados no sistema conseguem responder, pelo botão "Responder".</div>
                </div>
              </label>
              <label class="vis-option" :class="{ active: settings.visibility === 'public' }">
                <input type="radio" value="public" v-model="settings.visibility" />
                <div>
                  <div class="vis-option-title">Público</div>
                  <div class="vis-option-desc">Gera um link para qualquer pessoa responder informando um nome de usuário.</div>
                </div>
              </label>
            </div>
            <div v-if="detail.visibility === 'public' && detail.publicHash" class="link-field">
              <input class="field-input" :value="buildLink(detail.publicHash)" readonly @focus="($event.target as HTMLInputElement).select()" />
              <button class="btn-secondary" type="button" @click="copyLink(detail.publicHash)">{{ copiedHash === detail.publicHash ? 'Copiado!' : 'Copiar link' }}</button>
            </div>
            <p v-else-if="settings.visibility === 'public'" class="field-hint">O link será gerado ao salvar.</p>
          </section>

          <section class="form-card">
            <h3 class="form-title">Perguntas por tentativa</h3>
            <p class="field-hint field-hint--flush per-cat-intro">
              Defina, para cada categoria, quantas perguntas aparecem em cada tentativa e como elas são escolhidas.
              Salvo automaticamente junto com as perguntas.
            </p>
            <div class="table-wrap">
              <table class="att-table per-cat-table">
                <thead>
                  <tr><th>Categoria</th><th class="num">Cadastradas</th><th>Como escolher</th><th class="num">Quantidade</th><th class="num">Por tentativa</th></tr>
                </thead>
                <tbody>
                  <tr v-for="cat in editForm.categories" :key="cat.key" :class="{ 'row-muted': !cat.questions.length }">
                    <td class="per-cat-name">{{ cat.name || 'Sem nome' }}</td>
                    <td class="num">{{ cat.questions.length }}</td>
                    <td>
                      <select class="field-input mode-select" v-model="cat.mode">
                        <option value="all">Todas</option>
                        <option value="random">Sortear</option>
                        <option value="first">As primeiras</option>
                      </select>
                    </td>
                    <td class="num">
                      <input v-if="cat.mode !== 'all'" class="field-input count-input" type="number" min="1" :max="cat.questions.length || undefined" v-model.number="cat.questionCount" />
                      <span v-else class="muted">—</span>
                    </td>
                    <td class="num">
                      <strong>{{ effectiveCount(cat) }}</strong>
                      <span v-if="cat.mode !== 'all' && cat.questionCount > cat.questions.length" class="per-cat-warn" title="Quantidade maior que o total cadastrado: todas serão usadas">!</span>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="row-strong">
                    <td>Total</td>
                    <td class="num">{{ totalQuestions }}</td>
                    <td colspan="2">{{ answerableCategories }} etapa{{ answerableCategories === 1 ? '' : 's' }}</td>
                    <td class="num">{{ perAttemptCount }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div class="per-cat-status">
              <span class="status-dot" :class="'status-dot--' + saveState"></span>
              <span :class="{ 'save-status-text--err': saveState === 'error' }">{{ saveStatusText }}</span>
            </div>
          </section>

          <section class="form-card">
            <h3 class="form-title">Após o envio</h3>
            <label class="check-row">
              <input type="checkbox" v-model="settings.showAnswersAfterSubmit" />
              <span>Mostrar o gabarito (resposta correta) no resumo</span>
            </label>
            <p class="field-hint">Desligado: o participante vê só quais acertou e quais errou, sem a resposta certa — útil se ele puder tentar de novo.</p>
          </section>

          <div class="save-bar">
            <p v-if="settingsError" class="form-error">{{ settingsError }}</p>
            <span v-else></span>
            <button class="btn-save" type="button" :disabled="savingSettings" @click="saveSettings">{{ savingSettings ? 'Salvando...' : 'Salvar configurações' }}</button>
          </div>
        </div>

        <!-- ── Resultados ── -->
        <div v-else class="pane">
          <div class="res-toolbar">
            <div class="seg" role="tablist">
              <button class="seg-btn" :class="{ active: resultsView === 'summary' }" type="button" @click="resultsView = 'summary'">Relatório resumido</button>
              <button class="seg-btn" :class="{ active: resultsView === 'individual' }" type="button" @click="resultsView = 'individual'">Resultados individuais</button>
            </div>
            <div class="res-filters">
              <label class="filter">
                <span class="filter-label">De</span>
                <input class="field-input filter-input" type="date" v-model="filters.from" :max="filters.to || undefined" />
              </label>
              <label class="filter">
                <span class="filter-label">Até</span>
                <input class="field-input filter-input" type="date" v-model="filters.to" :min="filters.from || undefined" />
              </label>
              <label class="filter">
                <span class="filter-label">Dispositivo</span>
                <select class="field-input filter-input" v-model="filters.device">
                  <option value="all">Todos</option>
                  <option value="shared">Só compartilhados ({{ sharedDeviceIds.size }})</option>
                  <option v-for="d in deviceOptions" :key="d.id" :value="d.id">{{ d.label }} — {{ d.names }}</option>
                </select>
              </label>
              <label v-if="resultsView === 'individual'" class="filter filter--grow">
                <span class="filter-label">Usuário</span>
                <input class="field-input filter-input" v-model="filters.search" placeholder="Buscar nome" />
              </label>
              <button v-if="filtersActive" class="btn-link filter-clear" type="button" @click="clearFilters">Limpar filtros</button>
            </div>
          </div>

          <div v-if="attemptsLoading" class="empty-hint">Carregando...</div>
          <div v-else-if="attempts.length === 0" class="empty-hint empty-hint--compact">Nenhuma tentativa enviada ainda.</div>
          <div v-else-if="filteredAttempts.length === 0" class="empty-hint empty-hint--compact">Nenhuma tentativa no filtro selecionado.</div>
          <template v-else>
            <div class="stats-row">
              <div class="stat"><span class="stat-value">{{ filteredAttempts.length }}</span><span class="stat-label">Tentativas</span></div>
              <div class="stat"><span class="stat-value">{{ uniqueParticipants }}</span><span class="stat-label">Participantes</span></div>
              <div class="stat"><span class="stat-value">{{ formatPct(avgScore) }}</span><span class="stat-label">Média geral</span></div>
              <div class="stat" :class="{ 'stat--warn': sharedInFilter > 0 }">
                <span class="stat-value">{{ sharedInFilter }}</span>
                <span class="stat-label">Dispositivos com +1 nome</span>
              </div>
            </div>

            <!-- Relatório resumido: rosca de acertos médios, geral e por categoria -->
            <template v-if="resultsView === 'summary'">
              <div class="donut-grid">
                <figure v-for="item in donutItems" :key="item.key" class="donut-card" :class="{ 'donut-card--overall': item.overall }">
                  <svg class="donut" viewBox="0 0 100 100" role="img" :aria-label="`${item.name}: média de ${formatPct(item.avg)} de acertos`">
                    <title>{{ item.name }} — média {{ formatPct(item.avg) }} · {{ item.correct }} de {{ item.total }} respostas certas em {{ item.count }} tentativa(s)</title>
                    <circle v-for="seg in donutSegments(item.avg)" :key="seg.kind" cx="50" cy="50" :r="DONUT_R" fill="none" stroke-width="12"
                      :class="seg.kind === 'value' ? 'donut-value' : 'donut-rest'"
                      :stroke-dasharray="seg.dash" :stroke-dashoffset="seg.offset" transform="rotate(-90 50 50)" />
                    <text x="50" y="48" text-anchor="middle" class="donut-pct">{{ formatPct(item.avg) }}</text>
                    <text x="50" y="62" text-anchor="middle" class="donut-sub">acertos</text>
                  </svg>
                  <figcaption>
                    <span class="donut-name">{{ item.name }}</span>
                    <span class="donut-meta">{{ item.count }} tentativa{{ item.count === 1 ? '' : 's' }} · {{ item.correct }}/{{ item.total }} certas</span>
                  </figcaption>
                </figure>
              </div>
              <div class="donut-legend">
                <span><i class="lg lg--good"></i>Acertos (média)</span>
                <span><i class="lg lg--rest"></i>Erros</span>
              </div>

              <div class="table-wrap">
                <table class="att-table">
                  <thead>
                    <tr><th>Categoria</th><th class="num">Tentativas</th><th class="num">Média</th><th class="num">Menor</th><th class="num">Maior</th><th class="num">Respostas certas</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in donutItems" :key="item.key" :class="{ 'row-strong': item.overall }">
                      <td>{{ item.name }}</td>
                      <td class="num">{{ item.count }}</td>
                      <td class="num"><span class="pct-pill" :class="pctTone(item.avg)">{{ formatPct(item.avg) }}</span></td>
                      <td class="num">{{ formatPct(item.min) }}</td>
                      <td class="num">{{ formatPct(item.max) }}</td>
                      <td class="num">{{ item.correct }}/{{ item.total }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <!-- Resultados individuais -->
            <div v-else class="table-wrap">
              <table class="att-table">
                <thead>
                  <tr><th>Usuário</th><th>Dispositivo</th><th>Origem</th><th>Enviado em</th><th class="num">Acertos</th><th class="num">%</th></tr>
                </thead>
                <tbody>
                  <template v-for="a in filteredAttempts" :key="a.id">
                    <tr class="att-row" :class="{ open: openAttemptId === a.id }" @click="toggleAttempt(a.id)">
                      <td>
                        <svg class="row-caret" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><polyline points="9 18 15 12 9 6"/></svg>
                        {{ a.participantName }}
                      </td>
                      <td>
                        <template v-if="a.deviceId">
                          <button class="device-chip" :class="{ 'device-chip--shared': sharedDeviceIds.has(a.deviceId) }" type="button"
                            :title="deviceTooltip(a)" @click.stop="filters.device = a.deviceId">
                            {{ deviceLabel(a.deviceId) }}
                            <template v-if="sharedDeviceIds.has(a.deviceId)"> · {{ deviceNameCount(a.deviceId) }} nomes</template>
                          </button>
                        </template>
                        <span v-else class="muted">—</span>
                      </td>
                      <td><span class="src-chip">{{ a.source === 'public' ? 'Link público' : 'Interno' }}</span></td>
                      <td>{{ formatDateTime(a.submittedAt) }}</td>
                      <td class="num">{{ a.correctCount }}/{{ a.totalCount }}</td>
                      <td class="num"><span class="pct-pill" :class="pctTone(a.scorePercent)">{{ formatPct(a.scorePercent) }}</span></td>
                    </tr>
                    <tr v-if="openAttemptId === a.id" class="att-detail-row">
                      <td colspan="6">
                        <div v-if="sharedDeviceIds.has(a.deviceId)" class="shared-note">
                          Mesmo dispositivo usado por: {{ otherNamesOnDevice(a).join(', ') }}
                        </div>
                        <div v-if="a.categoryScores && a.categoryScores.length > 1" class="att-cats">
                          <span v-for="cs in a.categoryScores" :key="cs.categoryId || cs.name" class="att-cat-chip">
                            {{ cs.name }} <strong :class="pctTone(cs.scorePercent)">{{ formatPct(cs.scorePercent) }}</strong> <span class="muted">{{ cs.correctCount }}/{{ cs.totalCount }}</span>
                          </span>
                        </div>
                        <div v-if="attemptDetailLoading" class="detail-loading">Carregando respostas...</div>
                        <template v-else-if="attemptDetail">
                          <div v-for="group in attemptGroups" :key="group.key" class="att-group">
                            <div v-if="attemptGroups.length > 1" class="att-group-title">{{ group.name }}</div>
                            <ol class="att-answers">
                              <li v-for="r in group.items" :key="r.questionId" :class="r.isCorrect ? 'ok' : 'err'">
                                <div class="att-q">{{ r.text }}</div>
                                <div class="att-a">
                                  <span class="att-mark">{{ r.isCorrect ? '✓' : '✗' }}</span>
                                  Resposta: <strong>{{ r.selectedIndex === null ? 'sem resposta' : `${letter(r.selectedIndex)}) ${r.options[r.selectedIndex]}` }}</strong>
                                  <template v-if="!r.isCorrect && r.correctIndex !== undefined"> · Correta: {{ letter(r.correctIndex) }}) {{ r.options[r.correctIndex] }}</template>
                                </div>
                              </li>
                            </ol>
                          </div>
                        </template>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </template>
        </div>
        </div><!-- /detail-main -->

        <!-- ── Resumo lateral ── -->
        <aside v-if="detailTab !== 'resultados'" class="detail-aside">
          <div class="aside-card aside-hero">
            <span class="aside-eyebrow">Questionário</span>
            <h3 class="aside-title">{{ editForm.title || 'Sem título' }}</h3>
            <p v-if="editForm.description" class="aside-desc">{{ editForm.description }}</p>
            <div class="aside-chips">
              <span class="vis-chip" :class="detail.visibility === 'public' ? 'vis-chip--public' : 'vis-chip--private'">
                {{ detail.visibility === 'public' ? 'Público' : 'Fechado' }}
              </span>
              <span class="aside-chip">{{ perAttemptCount }} por tentativa</span>
            </div>
          </div>

          <div class="aside-card">
            <div class="aside-metrics">
              <div class="aside-metric">
                <span class="aside-metric-value">{{ totalQuestions }}</span>
                <span class="aside-metric-label">Pergunta{{ totalQuestions === 1 ? '' : 's' }}</span>
              </div>
              <div class="aside-metric">
                <span class="aside-metric-value">{{ editForm.categories.length }}</span>
                <span class="aside-metric-label">Categoria{{ editForm.categories.length === 1 ? '' : 's' }}</span>
              </div>
              <div class="aside-metric">
                <span class="aside-metric-value">{{ attempts.length }}</span>
                <span class="aside-metric-label">Tentativa{{ attempts.length === 1 ? '' : 's' }}</span>
              </div>
              <div class="aside-metric">
                <span class="aside-metric-value">{{ attempts.length ? formatPct(overallAvg) : '—' }}</span>
                <span class="aside-metric-label">Média</span>
              </div>
            </div>
            <div v-if="totalQuestions" class="aside-completeness">
              <div class="aside-completeness-head">
                <span>Perguntas completas</span>
                <strong>{{ completeCount }}/{{ totalQuestions }}</strong>
              </div>
              <div class="aside-bar"><div class="aside-bar-fill" :style="{ width: completePct + '%' }"></div></div>
            </div>
          </div>

          <div v-if="totalQuestions" class="aside-card">
            <div class="aside-section-title">Navegar pelas perguntas</div>
            <div class="q-nav-groups">
              <div v-for="cat in editForm.categories" :key="cat.key" class="q-nav-group">
                <button class="q-nav-cat" type="button" @click="goToCategory(cat)">{{ cat.name || 'Sem nome' }}</button>
                <div v-if="cat.questions.length" class="q-nav">
                  <button
                    v-for="(q, qi) in cat.questions"
                    :key="q.key"
                    type="button"
                    class="q-nav-dot"
                    :class="questionIssue(q) ? 'q-nav-dot--todo' : 'q-nav-dot--ok'"
                    :title="questionIssue(q) || `Pergunta ${qi + 1}`"
                    @click="goToQuestion(cat, q.key)"
                  >{{ qi + 1 }}</button>
                </div>
                <span v-else class="q-nav-empty">sem perguntas</span>
              </div>
            </div>
            <div class="q-nav-legend">
              <span><i class="lg lg--ok"></i>Completa</span>
              <span><i class="lg lg--todo"></i>Pendente</span>
            </div>
          </div>

          <div class="aside-card aside-status">
            <div class="aside-status-row">
              <span class="status-dot" :class="'status-dot--' + saveState"></span>
              <span>{{ saveStatusText }}</span>
            </div>
            <div v-if="detail.visibility === 'public' && detail.publicHash" class="aside-status-row aside-status-row--action">
              <button class="btn-link" type="button" @click="copyLink(detail.publicHash)">{{ copiedHash === detail.publicHash ? 'Link copiado!' : 'Copiar link público' }}</button>
            </div>
          </div>
        </aside>
        </div><!-- /detail-layout -->
      </template>
    </template>

    <!-- ════════════ RESPONDER (interno) ════════════ -->
    <template v-else-if="view === 'take'">
      <button class="back-link" type="button" @click="exitTake">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="15 18 9 12 15 6"/></svg>
        Sair do questionário
      </button>
      <div v-if="takeLoading" class="empty-hint">Preparando questionário...</div>
      <div v-else-if="takeError" class="empty-hint">{{ takeError }}</div>
      <div v-else class="take-wrap">
        <div class="take-head">
          <h2 class="tab-title">{{ take.title }}</h2>
          <p v-if="take.description && !take.result" class="tab-sub take-desc">{{ take.description }}</p>
        </div>
        <QuizRunner
          :categories="take.categories"
          :current-index="take.currentIndex"
          :result="take.result"
          :submitting="takeSubmitting"
          :participant-name="take.participantName"
          @submit-category="submitTakeCategory"
          @restart="startTake(take.quizId)"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'
import { sessionStore } from '@/store/session'
import { newId } from '@/utils/id'
import { deviceLabel, getQuizDeviceId } from '@/utils/quizDevice'
import * as XLSX from 'xlsx'
import QuizRunner, { QuizCategoryScore, QuizRunnerCategory, QuizRunnerResult, QuizRunnerResultItem } from './QuizRunner.vue'

interface QuizSummary {
  id: string; title: string; description: string
  visibility: 'public' | 'private'; publicHash: string; showAnswersAfterSubmit: boolean
  totalQuestions: number; categoriesCount: number; attemptsCount: number; avgScore: number | null
}
interface QuizQuestion { id: string; text: string; options: string[]; correctIndex: number }
interface QuizCategory { id: string; name: string; questionCount: number; selectionMode: 'random' | 'first'; questions: QuizQuestion[] }
interface QuizDetail {
  id: string; title: string; description: string
  visibility: 'public' | 'private'; publicHash: string; showAnswersAfterSubmit: boolean
  categories: QuizCategory[]
}
interface EditableQuestion { key: string; id: string; text: string; options: string[]; correctIndex: number }
// mode: 'all' = todas; 'random' = sorteia questionCount; 'first' = as questionCount primeiras na ordem
interface EditableCategory { key: string; id: string; name: string; mode: 'all' | 'random' | 'first'; questionCount: number; collapsed: boolean; questions: EditableQuestion[] }
interface Attempt {
  id: string; participantName: string; accountUsername: string; source: 'public' | 'internal'; deviceId: string
  categoryScores: QuizCategoryScore[]
  correctCount: number; totalCount: number; scorePercent: number; startedAt: string; submittedAt: string
}
interface ImportedQuestion { text: string; options: string[]; correctIndex: number }

const blankQuestion = (): EditableQuestion => ({ key: newId('k'), id: '', text: '', options: ['', '', '', ''], correctIndex: 0 })
const isBlankQuestion = (q: EditableQuestion) => !q.text.trim() && !q.options.some(o => o.trim())
const DONUT_R = 40
const DONUT_C = 2 * Math.PI * DONUT_R

export default defineComponent({
  name: 'OfficeQuestionariosTab',
  components: { QuizRunner },
  setup() {
    const view = ref<'list' | 'detail' | 'take'>('list')

    // ── Lista ──
    const quizzes = ref<QuizSummary[]>([])
    const loading = ref(false)
    const showCreate = ref(false)
    const createForm = ref({ title: '', description: '' })
    const createError = ref('')
    const creating = ref(false)
    const createTitleRef = ref<HTMLInputElement | null>(null)
    const copiedHash = ref('')

    const load = async () => {
      loading.value = true
      try { quizzes.value = await apiFetch<QuizSummary[]>('/office/quizzes') }
      catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao carregar questionários.', 'error') }
      finally { loading.value = false }
    }

    const openCreate = async () => {
      createForm.value = { title: '', description: '' }
      createError.value = ''
      showCreate.value = true
      await nextTick()
      createTitleRef.value?.focus()
    }

    const create = async () => {
      if (!createForm.value.title.trim()) { createError.value = 'Informe um título.'; return }
      creating.value = true; createError.value = ''
      try {
        const res = await apiFetch<{ id: string }>('/office/quizzes', { method: 'POST', body: JSON.stringify(createForm.value) })
        showCreate.value = false
        await openQuiz(res.id)
        const first = editForm.value.categories[0]
        if (first && first.questions.length === 0) first.questions.push(blankQuestion())
      } catch (e: unknown) { createError.value = e instanceof Error ? e.message : 'Erro ao criar.' } finally { creating.value = false }
    }

    const buildLink = (hash: string) => `${window.location.origin}/questionario/${hash}`

    const copyLink = async (hash: string) => {
      try {
        await navigator.clipboard.writeText(buildLink(hash))
        copiedHash.value = hash
        uiStore.toast('Link copiado.', 'success')
        window.setTimeout(() => { if (copiedHash.value === hash) copiedHash.value = '' }, 2000)
      } catch {
        uiStore.toast('Não foi possível copiar o link.', 'error')
      }
    }

    const removeQuiz = async (q: QuizSummary) => {
      const ok = await uiStore.confirm(`Excluir o questionário "${q.title}"? Todas as perguntas e resultados serão apagados.`)
      if (!ok) return
      try {
        await apiFetch(`/office/quizzes/${q.id}`, { method: 'DELETE' })
        uiStore.toast('Questionário excluído.', 'success')
        await load()
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao excluir.', 'error') }
    }

    // ── Detalhe ──
    const detail = ref<QuizDetail | null>(null)
    const detailLoading = ref(false)
    const detailTab = ref<'perguntas' | 'config' | 'resultados'>('perguntas')
    const editForm = ref<{ title: string; description: string; categories: EditableCategory[] }>({ title: '', description: '', categories: [] })
    const saving = ref(false)
    const saveError = ref('')
    const lastSavedSnapshot = ref('')
    const lastSavedAt = ref<Date | null>(null)
    const settings = ref({ visibility: 'private' as 'public' | 'private', showAnswersAfterSubmit: false })
    const settingsError = ref('')
    const savingSettings = ref(false)

    const effectiveCount = (cat: EditableCategory) => (cat.mode !== 'all' && cat.questionCount > 0 ? Math.min(cat.questionCount, cat.questions.length) : cat.questions.length)
    const totalQuestions = computed(() => editForm.value.categories.reduce((s, c) => s + c.questions.length, 0))
    const perAttemptCount = computed(() => editForm.value.categories.reduce((s, c) => s + effectiveCount(c), 0))
    const answerableCategories = computed(() => editForm.value.categories.filter(c => c.questions.length > 0).length)

    // "Sujo" = conteúdo atual difere do último salvo. Usa as keys (estáveis) e não os ids,
    // que mudam quando o servidor atribui id a uma categoria/pergunta nova. Recolher/expandir não conta.
    const snapshotOf = () => JSON.stringify([
      editForm.value.title.trim(),
      editForm.value.description.trim(),
      editForm.value.categories.map(c => [c.key, c.name.trim(), c.mode, c.mode === 'all' ? 0 : c.questionCount, c.questions.map(q => [q.key, q.text, q.options, q.correctIndex])])
    ])
    const currentSnapshot = computed(snapshotOf)
    const dirty = computed(() => !!detail.value && currentSnapshot.value !== lastSavedSnapshot.value)

    const questionIssue = (q: EditableQuestion) => {
      if (!q.text.trim()) return 'enunciado vazio'
      if (q.options.length < 2) return 'precisa de pelo menos 2 alternativas'
      if (q.options.some(o => !o.trim())) return 'há alternativa vazia'
      if (q.correctIndex < 0 || q.correctIndex >= q.options.length) return 'marque a alternativa correta'
      return ''
    }
    const pendingInCategory = (cat: EditableCategory) => cat.questions.filter(q => questionIssue(q)).length
    const filledQuestions = (cat: EditableCategory) => cat.questions.filter(q => !isBlankQuestion(q))
    const validationError = computed(() => {
      if (!editForm.value.title.trim()) return 'informe um título'
      for (let ci = 0; ci < editForm.value.categories.length; ci++) {
        const cat = editForm.value.categories[ci]
        if (!cat.name.trim()) return `categoria ${ci + 1}: informe um nome`
        if (cat.mode !== 'all' && (!Number.isInteger(cat.questionCount) || cat.questionCount < 1)) return `categoria "${cat.name}": informe quantas perguntas aparecem por tentativa`
        const qi = cat.questions.findIndex(q => questionIssue(q))
        if (qi >= 0) return `categoria "${cat.name}", pergunta ${qi + 1}: ${questionIssue(cat.questions[qi])}`
      }
      return ''
    })

    const applyDetail = (d: QuizDetail) => {
      detail.value = d
      editForm.value = {
        title: d.title,
        description: d.description,
        categories: d.categories.map((c, ci) => ({
          key: c.id,
          id: c.id,
          name: c.name,
          mode: c.questionCount > 0 ? (c.selectionMode === 'first' ? 'first' : 'random') : 'all',
          questionCount: c.questionCount > 0 ? c.questionCount : Math.min(10, Math.max(1, c.questions.length)),
          // com várias categorias, abre só a primeira (provas grandes ficam navegáveis)
          collapsed: d.categories.length > 1 && ci > 0,
          questions: c.questions.map(q => ({ key: q.id, id: q.id, text: q.text, options: [...q.options], correctIndex: q.correctIndex }))
        }))
      }
      settings.value = { visibility: d.visibility, showAnswersAfterSubmit: d.showAnswersAfterSubmit }
      lastSavedSnapshot.value = snapshotOf()
      lastSavedAt.value = null
    }

    const openQuiz = async (id: string, tab: 'perguntas' | 'config' | 'resultados' = 'perguntas') => {
      clearAutosave()
      view.value = 'detail'
      detailTab.value = tab
      detailLoading.value = true
      attempts.value = []
      openAttemptId.value = ''
      importPreview.value = null
      clearFilters()
      saveError.value = ''; settingsError.value = ''
      try {
        applyDetail(await apiFetch<QuizDetail>(`/office/quizzes/${id}`))
        await loadAttempts()
      } catch (e: unknown) {
        uiStore.toast(e instanceof Error ? e.message : 'Erro ao abrir questionário.', 'error')
        view.value = 'list'
      } finally { detailLoading.value = false }
    }

    // Antes de sair do editor: tenta salvar; só pergunta se não deu (pendência/erro).
    const flushBeforeLeave = async () => {
      if (!dirty.value) return true
      if (await saveQuestions(true)) return true
      return uiStore.confirm(`Há alterações não salvas (${validationError.value || saveError.value || 'erro ao salvar'}). Sair mesmo assim?`)
    }

    const backToList = async () => {
      if (!(await flushBeforeLeave())) return
      clearAutosave()
      view.value = 'list'
      detail.value = null
      await load()
    }

    const letter = (i: number) => String.fromCharCode(65 + i)

    // ── Categorias ──
    const addCategory = async () => {
      const cat: EditableCategory = {
        key: newId('k'), id: '', name: `Categoria ${editForm.value.categories.length + 1}`,
        mode: 'all', questionCount: 10, collapsed: false, questions: [blankQuestion()]
      }
      editForm.value.categories.push(cat)
      await nextTick()
      scrollToElement(document.getElementById(`qcat-${cat.key}`), 'start')
    }
    const removeCategory = async (index: number) => {
      const cat = editForm.value.categories[index]
      if (editForm.value.categories.length <= 1) return
      if (filledQuestions(cat).length) {
        const ok = await uiStore.confirm(`Excluir a categoria "${cat.name}" e suas ${filledQuestions(cat).length} pergunta(s)?`)
        if (!ok) return
      }
      editForm.value.categories.splice(index, 1)
    }
    const moveCategory = (index: number, delta: number) => {
      const list = editForm.value.categories
      const target = index + delta
      if (target < 0 || target >= list.length) return
      const [item] = list.splice(index, 1)
      list.splice(target, 0, item)
    }
    const setAllCollapsed = (collapsed: boolean) => { editForm.value.categories.forEach(c => { c.collapsed = collapsed }) }

    // ── Perguntas ──
    const addQuestion = (cat: EditableCategory) => { cat.questions.push(blankQuestion()) }
    const removeQuestion = async (cat: EditableCategory, index: number) => {
      if (!isBlankQuestion(cat.questions[index])) {
        const ok = await uiStore.confirm('Remover esta pergunta?')
        if (!ok) return
      }
      cat.questions.splice(index, 1)
    }
    const moveQuestion = (cat: EditableCategory, index: number, delta: number) => {
      const target = index + delta
      if (target < 0 || target >= cat.questions.length) return
      const [item] = cat.questions.splice(index, 1)
      cat.questions.splice(target, 0, item)
    }
    const duplicateQuestion = (cat: EditableCategory, index: number) => {
      const src = cat.questions[index]
      cat.questions.splice(index + 1, 0, { key: newId('k'), id: '', text: src.text, options: [...src.options], correctIndex: src.correctIndex })
    }
    const removeOption = (q: EditableQuestion, oi: number) => {
      if (q.options.length <= 2) return
      q.options.splice(oi, 1)
      if (q.correctIndex === oi) q.correctIndex = 0
      else if (q.correctIndex > oi) q.correctIndex -= 1
    }

    // Salva sem recarregar o editor (não tira o foco de quem está digitando): o servidor
    // devolve os ids na ordem enviada e eles são gravados nas categorias/perguntas enviadas.
    // auto = salvamento automático: silencioso e não salva se houver pendência.
    const saveQuestions = async (auto = false): Promise<boolean> => {
      if (!detail.value || saving.value) return false
      if (validationError.value) {
        if (!auto) saveError.value = validationError.value.charAt(0).toUpperCase() + validationError.value.slice(1) + '.'
        return false
      }
      const sentCats = editForm.value.categories.slice()
      const sentQs = sentCats.map(c => c.questions.slice())
      const snapshot = currentSnapshot.value
      saving.value = true; saveError.value = ''
      try {
        const res = await apiFetch<{ categoryIds: string[]; questionIds: string[][] }>(`/office/quizzes/${detail.value.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            title: editForm.value.title,
            description: editForm.value.description,
            categories: sentCats.map((c, ci) => ({
              id: c.id,
              name: c.name,
              questionCount: c.mode === 'all' ? 0 : c.questionCount,
              selectionMode: c.mode === 'first' ? 'first' : 'random',
              questions: sentQs[ci].map(q => ({ id: q.id, text: q.text, options: q.options, correctIndex: q.correctIndex }))
            }))
          })
        })
        sentCats.forEach((c, ci) => {
          if (res.categoryIds?.[ci]) c.id = res.categoryIds[ci]
          sentQs[ci].forEach((q, qi) => { const id = res.questionIds?.[ci]?.[qi]; if (id) q.id = id })
        })
        if (detail.value) detail.value.title = editForm.value.title
        lastSavedSnapshot.value = snapshot
        lastSavedAt.value = new Date()
        if (!auto) uiStore.toast('Perguntas salvas.', 'success')
        return true
      } catch (e: unknown) {
        saveError.value = e instanceof Error ? e.message : 'Erro ao salvar.'
        return false
      } finally { saving.value = false }
    }

    // ── Salvamento automático ──
    // Até 15s depois da primeira alteração não salva (sem reiniciar o relógio a cada tecla).
    const AUTOSAVE_MS = 15000
    let autosaveTimer: number | undefined
    const clearAutosave = () => { if (autosaveTimer) { window.clearTimeout(autosaveTimer); autosaveTimer = undefined } }
    const scheduleAutosave = () => {
      if (autosaveTimer || !dirty.value) return
      autosaveTimer = window.setTimeout(async () => {
        autosaveTimer = undefined
        if (view.value !== 'detail' || !dirty.value) return
        const ok = await saveQuestions(true)
        if (ok && dirty.value) scheduleAutosave() // houve edição durante o envio
      }, AUTOSAVE_MS)
    }
    watch(currentSnapshot, () => { saveError.value = ''; scheduleAutosave() })

    const saveState = computed<'saving' | 'error' | 'pending' | 'dirty' | 'saved'>(() => {
      if (saving.value) return 'saving'
      if (saveError.value) return 'error'
      if (dirty.value) return validationError.value ? 'pending' : 'dirty'
      return 'saved'
    })
    const saveStatusText = computed(() => {
      switch (saveState.value) {
        case 'saving': return 'Salvando…'
        case 'error': return saveError.value
        case 'pending': return `Não salvo — ${validationError.value}`
        case 'dirty': return 'Alterações não salvas · salvamento automático em instantes'
        default: return lastSavedAt.value
          ? `Salvo automaticamente às ${lastSavedAt.value.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
          : 'Todas as alterações salvas'
      }
    })

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (view.value === 'detail' && dirty.value) { e.preventDefault(); e.returnValue = '' }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      clearAutosave()
      // trocou de aba do escritório com alterações válidas: salva em segundo plano
      if (view.value === 'detail' && dirty.value && !validationError.value) void saveQuestions(true)
    })

    // ── Resumo lateral / navegação ──
    const completeCount = computed(() => editForm.value.categories.reduce((s, c) => s + c.questions.filter(q => !questionIssue(q)).length, 0))
    const completePct = computed(() => totalQuestions.value ? Math.round((completeCount.value / totalQuestions.value) * 100) : 0)
    const flashKey = ref('')

    // rola só o container com scroll mais próximo (scrollIntoView também moveria ancestrais
    // com overflow: hidden, deslocando navbar/sidebar)
    const scrollToElement = (el: HTMLElement | null, align: 'center' | 'start' = 'center') => {
      let scroller = el?.parentElement || null
      while (scroller && !(/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight)) {
        scroller = scroller.parentElement
      }
      if (!el || !scroller) return
      const offset = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top
      const top = align === 'start' ? scroller.scrollTop + offset - 12 : scroller.scrollTop + offset - scroller.clientHeight / 2 + el.offsetHeight / 2
      scroller.scrollTo({ top, behavior: 'smooth' })
    }
    const ensurePerguntasTab = async () => {
      if (detailTab.value !== 'perguntas') { detailTab.value = 'perguntas'; await nextTick() }
    }
    const goToCategory = async (cat: EditableCategory) => {
      await ensurePerguntasTab()
      cat.collapsed = false
      await nextTick()
      scrollToElement(document.getElementById(`qcat-${cat.key}`), 'start')
    }
    const goToQuestion = async (cat: EditableCategory, key: string) => {
      await ensurePerguntasTab()
      cat.collapsed = false
      await nextTick()
      scrollToElement(document.getElementById(`qe-${key}`))
      flashKey.value = key
      window.setTimeout(() => { if (flashKey.value === key) flashKey.value = '' }, 1400)
    }

    // ── Importar do Excel (por categoria) ──
    // Layout (sem cabeçalho): A = pergunta, B = letra da alternativa correta ("A", "B"…), C em diante = alternativas.
    const importInputRef = ref<HTMLInputElement | null>(null)
    const importTargetKey = ref('')
    const importPreview = ref<{ categoryKey: string; fileName: string; questions: ImportedQuestion[]; errors: { row: number; message: string }[]; skippedHeader: boolean } | null>(null)
    const importMode = ref<'append' | 'replace'>('append')

    // Coluna B traz a letra da alternativa (A, B, C…), contada a partir da coluna C:
    // A = coluna C, B = coluna D, C = coluna E… Aceita "a", "B)", "C." e "Alternativa D".
    const OPTION_FIRST_COL = 2
    const answerLetterToColumn = (raw: string): number => {
      const m = raw.trim().toUpperCase().match(/^(?:ALTERNATIVA\s+)?([A-Z])\s*[).]?$/)
      return m ? OPTION_FIRST_COL + (m[1].charCodeAt(0) - 65) : -1
    }
    const sheetColumnLetter = (col: number) => String.fromCharCode(65 + col)

    const parseQuizSheet = (rows: unknown[][]) => {
      const questions: ImportedQuestion[] = []
      const errors: { row: number; message: string }[] = []
      let skippedHeader = false
      rows.forEach((rawRow, i) => {
        const rowNum = i + 1
        const cells = (rawRow || []).map(c => `${c ?? ''}`.trim())
        if (cells.every(c => !c)) return
        const text = cells[0] || ''
        const correctCol = answerLetterToColumn(cells[1] || '')
        const filled = cells.slice(OPTION_FIRST_COL).filter(Boolean)
        // Linha 1 é cabeçalho se B não for uma letra válida ou se as alternativas forem só os rótulos "A", "B", "C"…
        const looksLikeLetterHeader = filled.length >= 2 && filled.every((c, idx) => c.toUpperCase() === letter(idx))
        if (rowNum === 1 && (correctCol < 0 || looksLikeLetterHeader)) { skippedHeader = true; return }
        if (!text) { errors.push({ row: rowNum, message: 'pergunta (coluna A) vazia' }); return }
        if (correctCol < 0) { errors.push({ row: rowNum, message: `coluna B deve ter a letra da alternativa correta (A, B, C…), veio "${cells[1] || ''}"` }); return }
        const options: string[] = []
        let correctIndex = -1
        for (let col = OPTION_FIRST_COL; col < cells.length; col++) {
          if (!cells[col]) continue
          if (col === correctCol) correctIndex = options.length
          options.push(cells[col])
        }
        if (options.length < 2) { errors.push({ row: rowNum, message: 'precisa de pelo menos 2 alternativas (coluna C em diante)' }); return }
        if (options.length > 10) { errors.push({ row: rowNum, message: `máximo de 10 alternativas, encontradas ${options.length}` }); return }
        if (correctIndex < 0) {
          errors.push({ row: rowNum, message: `a alternativa correta "${letter(correctCol - OPTION_FIRST_COL)}" (coluna ${sheetColumnLetter(correctCol)}) está vazia` })
          return
        }
        questions.push({ text, options, correctIndex })
      })
      return { questions, errors, skippedHeader }
    }

    const pickImportFile = (categoryKey: string) => {
      importTargetKey.value = categoryKey
      importInputRef.value?.click()
    }

    const onImportFile = async (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      input.value = ''
      if (!file || !importTargetKey.value) return
      try {
        const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        if (!sheetName) throw new Error('A planilha não possui abas.')
        const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], { header: 1, defval: '', raw: false, blankrows: true })
        const parsed = parseQuizSheet(rows)
        if (!parsed.questions.length && !parsed.errors.length) throw new Error('Nenhuma pergunta encontrada na primeira aba da planilha.')
        const cat = editForm.value.categories.find(c => c.key === importTargetKey.value)
        if (cat) cat.collapsed = false
        importMode.value = 'append'
        importPreview.value = { categoryKey: importTargetKey.value, fileName: file.name, ...parsed }
      } catch (e: unknown) {
        uiStore.toast(e instanceof Error ? e.message : 'Não foi possível ler a planilha.', 'error')
      }
    }

    const applyImport = () => {
      const preview = importPreview.value
      const cat = preview && editForm.value.categories.find(c => c.key === preview.categoryKey)
      if (!preview || !cat) { importPreview.value = null; return }
      const imported = preview.questions.map(q => ({ key: newId('k'), id: '', text: q.text, options: [...q.options], correctIndex: q.correctIndex }))
      // descarta perguntas totalmente em branco (ex: a criada automaticamente numa categoria nova)
      cat.questions = importMode.value === 'replace' ? imported : [...filledQuestions(cat), ...imported]
      uiStore.toast(`${imported.length} pergunta(s) importada(s) em "${cat.name}". Elas serão salvas automaticamente em instantes.`, 'success')
      importPreview.value = null
    }

    const downloadTemplate = () => {
      const sheet = XLSX.utils.aoa_to_sheet([
        ['Qual EPI é obrigatório para trabalho em altura acima de 2 m?', 'B', 'Luva de raspa', 'Cinto de segurança tipo paraquedista', 'Protetor auricular', 'Óculos de proteção'],
        ['Quantas alternativas uma pergunta pode ter?', 'C', 'Apenas 1', 'Exatamente 4', 'De 2 a 10']
      ])
      sheet['!cols'] = [{ wch: 60 }, { wch: 8 }, { wch: 28 }, { wch: 34 }, { wch: 22 }, { wch: 22 }]
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, sheet, 'Perguntas')
      XLSX.writeFile(workbook, 'modelo-questionario.xlsx')
    }

    const saveSettings = async () => {
      if (!detail.value) return
      // a quantidade por categoria vive junto das perguntas: salva já se houver pendência
      if (dirty.value) await saveQuestions()
      savingSettings.value = true; settingsError.value = ''
      try {
        const res = await apiFetch<Omit<QuizDetail, 'categories'>>(`/office/quizzes/${detail.value.id}/settings`, {
          method: 'PUT',
          body: JSON.stringify(settings.value)
        })
        detail.value = { ...detail.value, ...res, categories: detail.value.categories }
        uiStore.toast(res.visibility === 'public' ? 'Configurações salvas. Link público ativo.' : 'Configurações salvas.', 'success')
      } catch (e: unknown) { settingsError.value = e instanceof Error ? e.message : 'Erro ao salvar.' } finally { savingSettings.value = false }
    }

    // ── Resultados ──
    const attempts = ref<Attempt[]>([])
    const attemptsLoading = ref(false)
    const resultsView = ref<'summary' | 'individual'>('summary')
    const filters = ref({ from: '', to: '', device: 'all', search: '' })
    const openAttemptId = ref('')
    const attemptDetail = ref<{ results: QuizRunnerResultItem[] } | null>(null)
    const attemptDetailLoading = ref(false)

    const loadAttempts = async () => {
      if (!detail.value) return
      attemptsLoading.value = true
      try { attempts.value = await apiFetch<Attempt[]>(`/office/quizzes/${detail.value.id}/attempts`) }
      finally { attemptsLoading.value = false }
    }

    const openResults = async () => {
      detailTab.value = 'resultados'
      await loadAttempts()
    }

    const clearFilters = () => { filters.value = { from: '', to: '', device: 'all', search: '' } }
    const filtersActive = computed(() => !!(filters.value.from || filters.value.to || filters.value.device !== 'all' || filters.value.search.trim()))

    // Dispositivos: quem respondeu é identificado pela conta (interno) ou pelo nome digitado (público).
    const participantKey = (a: Attempt) => a.accountUsername || `pub:${a.participantName.trim().toLowerCase()}`
    const namesByDevice = computed(() => {
      const map = new Map<string, Map<string, string>>()
      attempts.value.forEach(a => {
        if (!a.deviceId) return
        const names = map.get(a.deviceId) || new Map<string, string>()
        names.set(participantKey(a), a.participantName)
        map.set(a.deviceId, names)
      })
      return map
    })
    const sharedDeviceIds = computed(() => new Set([...namesByDevice.value.entries()].filter(([, names]) => names.size > 1).map(([id]) => id)))
    const deviceNameCount = (deviceId: string) => namesByDevice.value.get(deviceId)?.size || 0
    const otherNamesOnDevice = (a: Attempt) => [...(namesByDevice.value.get(a.deviceId)?.entries() || [])].filter(([key]) => key !== participantKey(a)).map(([, name]) => name)
    const deviceTooltip = (a: Attempt) => {
      const others = otherNamesOnDevice(a)
      return others.length ? `Mesmo dispositivo também usado por: ${others.join(', ')} — clique para filtrar` : 'Clique para ver só este dispositivo'
    }
    // no select: dispositivos com mais de um nome + o que estiver selecionado pelo chip da tabela
    const deviceOptions = computed(() => [...namesByDevice.value.entries()]
      .filter(([id, names]) => names.size > 1 || id === filters.value.device)
      .map(([id, names]) => ({ id, label: deviceLabel(id), names: [...names.values()].join(', ') })))

    const localDay = (iso: string) => {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
    const filteredAttempts = computed(() => {
      const { from, to, device, search } = filters.value
      const term = search.trim().toLowerCase()
      return attempts.value.filter(a => {
        const day = localDay(a.submittedAt)
        if (from && day < from) return false
        if (to && day > to) return false
        if (device === 'shared' && !sharedDeviceIds.value.has(a.deviceId)) return false
        if (device !== 'all' && device !== 'shared' && a.deviceId !== device) return false
        if (resultsView.value === 'individual' && term && !a.participantName.toLowerCase().includes(term)) return false
        return true
      })
    })

    const uniqueParticipants = computed(() => new Set(filteredAttempts.value.map(participantKey)).size)
    const avgScore = computed(() => filteredAttempts.value.length ? filteredAttempts.value.reduce((s, a) => s + a.scorePercent, 0) / filteredAttempts.value.length : 0)
    const overallAvg = computed(() => attempts.value.length ? attempts.value.reduce((s, a) => s + a.scorePercent, 0) / attempts.value.length : 0)
    const sharedInFilter = computed(() => new Set(filteredAttempts.value.filter(a => sharedDeviceIds.value.has(a.deviceId)).map(a => a.deviceId)).size)

    // Relatório resumido: média de acertos geral + por categoria (na ordem do questionário).
    const donutItems = computed(() => {
      type Agg = { key: string; name: string; overall: boolean; sum: number; count: number; min: number; max: number; correct: number; total: number }
      const make = (key: string, name: string, overall = false): Agg => ({ key, name, overall, sum: 0, count: 0, min: 100, max: 0, correct: 0, total: 0 })
      const add = (agg: Agg, pct: number, correct: number, total: number) => {
        agg.sum += pct; agg.count++; agg.min = Math.min(agg.min, pct); agg.max = Math.max(agg.max, pct)
        agg.correct += correct; agg.total += total
      }
      const overall = make('_overall', 'Geral', true)
      const cats = new Map<string, Agg>()
      editForm.value.categories.forEach(c => { if (c.id) cats.set(c.id, make(c.id, c.name)) })
      filteredAttempts.value.forEach(a => {
        add(overall, a.scorePercent, a.correctCount, a.totalCount)
        ;(a.categoryScores || []).forEach(cs => {
          const key = cs.categoryId || `name:${cs.name}`
          const agg = cats.get(key) || make(key, cs.name)
          cats.set(key, agg)
          add(agg, cs.scorePercent, cs.correctCount, cs.totalCount)
        })
      })
      const withAvg = (agg: Agg) => ({ ...agg, avg: agg.count ? agg.sum / agg.count : 0, min: agg.count ? agg.min : 0 })
      const catItems = [...cats.values()].filter(c => c.count > 0).map(withAvg)
      // uma categoria só repetiria o "Geral"
      return catItems.length > 1 ? [withAvg(overall), ...catItems] : [withAvg(overall)]
    })

    // Rosca: arco de acertos + resto, com 2px de folga entre os dois.
    const donutSegments = (pct: number) => {
      const p = Math.max(0, Math.min(100, pct)) / 100
      if (p <= 0) return [{ kind: 'rest', dash: `${DONUT_C} 0`, offset: 0 }]
      if (p >= 1) return [{ kind: 'value', dash: `${DONUT_C} 0`, offset: 0 }]
      const gap = 2
      const valueLen = Math.max(0.5, p * DONUT_C - gap)
      const restLen = Math.max(0.5, (1 - p) * DONUT_C - gap)
      return [
        { kind: 'value', dash: `${valueLen} ${DONUT_C - valueLen}`, offset: -gap / 2 },
        { kind: 'rest', dash: `${restLen} ${DONUT_C - restLen}`, offset: -(p * DONUT_C + gap / 2) }
      ]
    }

    const toggleAttempt = async (id: string) => {
      if (openAttemptId.value === id) { openAttemptId.value = ''; return }
      if (!detail.value) return
      openAttemptId.value = id
      attemptDetail.value = null
      attemptDetailLoading.value = true
      try { attemptDetail.value = await apiFetch(`/office/quizzes/${detail.value.id}/attempts/${id}`) }
      catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao carregar respostas.', 'error') }
      finally { attemptDetailLoading.value = false }
    }
    const attemptGroups = computed(() => {
      const groups: { key: string; name: string; items: QuizRunnerResultItem[] }[] = []
      ;(attemptDetail.value?.results || []).forEach(r => {
        const key = r.categoryId || '_'
        let g = groups.find(x => x.key === key)
        if (!g) { g = { key, name: r.categoryName || '', items: [] }; groups.push(g) }
        g.items.push(r)
      })
      return groups
    })

    const pctTone = (p: number) => (p >= 70 ? 'tone-ok' : p >= 50 ? 'tone-mid' : 'tone-low')
    const formatPct = (v: number | null) => `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    const formatDateTime = (iso: string) => {
      const d = new Date(iso)
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    }

    // ── Responder (interno, uma categoria por vez) ──
    const take = ref<{ quizId: string; attemptId: string; title: string; description: string; participantName: string; categories: QuizRunnerCategory[]; currentIndex: number; result: QuizRunnerResult | null }>({
      quizId: '', attemptId: '', title: '', description: '', participantName: '', categories: [], currentIndex: 0, result: null
    })
    const takeLoading = ref(false)
    const takeError = ref('')
    const takeSubmitting = ref(false)
    const takeReturnTo = ref<'list' | 'detail'>('list')

    const startTake = async (quizId: string) => {
      if (view.value !== 'take') takeReturnTo.value = view.value === 'detail' ? 'detail' : 'list'
      if (view.value === 'detail' && !(await flushBeforeLeave())) return
      clearAutosave()
      view.value = 'take'
      takeLoading.value = true; takeError.value = ''
      try {
        const res = await apiFetch<{ attemptId: string; title: string; description: string; categories: QuizRunnerCategory[] }>(`/office/quizzes/${quizId}/start`, {
          method: 'POST', body: JSON.stringify({ deviceId: getQuizDeviceId() })
        })
        take.value = {
          quizId, attemptId: res.attemptId, title: res.title, description: res.description,
          participantName: sessionStore.state.user?.name || sessionStore.state.user?.username || '',
          categories: res.categories, currentIndex: 0, result: null
        }
      } catch (e: unknown) { takeError.value = e instanceof Error ? e.message : 'Erro ao iniciar.' } finally { takeLoading.value = false }
    }

    const submitTakeCategory = async (payload: { categoryId: string; answers: Record<string, number> }) => {
      takeSubmitting.value = true
      try {
        const res = await apiFetch<({ done: false } | ({ done: true } & QuizRunnerResult))>(`/office/quizzes/${take.value.quizId}/submit`, {
          method: 'POST', body: JSON.stringify({ attemptId: take.value.attemptId, ...payload })
        })
        if (res.done) take.value.result = res
        else take.value.currentIndex += 1
      } catch (e: unknown) { uiStore.toast(e instanceof Error ? e.message : 'Erro ao enviar.', 'error') } finally { takeSubmitting.value = false }
    }

    const exitTake = async () => {
      if (!take.value.result && take.value.categories.length) {
        const ok = await uiStore.confirm('Sair sem terminar? As categorias ainda não enviadas serão perdidas.')
        if (!ok) return
      }
      if (takeReturnTo.value === 'detail' && detail.value?.id === take.value.quizId) {
        view.value = 'detail'
        await loadAttempts()
      } else {
        view.value = 'list'
        await load()
      }
    }

    onMounted(load)

    return {
      view, quizzes, loading, showCreate, createForm, createError, creating, createTitleRef, copiedHash,
      openCreate, create, buildLink, copyLink, removeQuiz,
      detail, detailLoading, detailTab, editForm, dirty, saving, saveError, saveState, saveStatusText,
      totalQuestions, perAttemptCount, answerableCategories, effectiveCount, questionIssue, pendingInCategory, filledQuestions,
      completeCount, completePct, flashKey, goToQuestion, goToCategory,
      settings, settingsError, savingSettings, openQuiz, backToList, letter,
      addCategory, removeCategory, moveCategory, setAllCollapsed,
      addQuestion, removeQuestion, moveQuestion, duplicateQuestion, removeOption, saveQuestions, saveSettings,
      importInputRef, importPreview, importMode, pickImportFile, onImportFile, applyImport, downloadTemplate,
      attempts, attemptsLoading, resultsView, filters, filtersActive, clearFilters, filteredAttempts,
      sharedDeviceIds, deviceNameCount, otherNamesOnDevice, deviceTooltip, deviceOptions, deviceLabel,
      uniqueParticipants, avgScore, overallAvg, sharedInFilter, donutItems, donutSegments, DONUT_R,
      openAttemptId, attemptDetail, attemptDetailLoading, attemptGroups, openResults, toggleAttempt,
      pctTone, formatPct, formatDateTime,
      take, takeLoading, takeError, takeSubmitting, startTake, submitTakeCategory, exitTake
    }
  }
})
</script>

<style scoped lang="scss">
.tab-shell { width: 100%; }
.tab-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
.tab-title { margin: 0; font-size: 18px; font-weight: 800; color: var(--ink, #2c2b27); }
.tab-sub { margin: 4px 0 0; font-size: 13px; color: var(--ink-muted, #888); }
.head-actions { display: flex; gap: 8px; }
.detail-title-wrap { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
  font-size: 13px; font-weight: 600;
  &:hover { opacity: .88; }
}
.btn-secondary {
  padding: 7px 14px; border-radius: 7px; cursor: pointer; white-space: nowrap;
  border: 1.5px solid var(--border-strong, rgba(31,30,26,.18)); background: var(--surface, #fff);
  color: var(--ink, #2c2b27); font-size: 13px; font-weight: 600;
  &:hover:not(:disabled) { background: var(--accent-light, #deecf9); }
  &:disabled { opacity: .5; cursor: not-allowed; }
}
.btn-save {
  padding: 8px 18px; border-radius: 7px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 13px; font-weight: 600;
  &:disabled { opacity: .6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: .88; }
}
.btn-cancel {
  padding: 8px 14px; border-radius: 7px; border: 1.5px solid var(--border-strong, rgba(31,30,26,.18));
  background: var(--surface, #fff); color: var(--ink, #2c2b27); cursor: pointer; font-size: 13px; font-weight: 500;
  &:hover { background: rgba(31,30,26,.04); }
}

.form-card { background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1)); border-radius: 14px; padding: 18px 20px; margin-bottom: 16px; }
.form-title { margin: 0 0 14px; font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; &--single { grid-template-columns: 1fr; } }
.field { display: flex; flex-direction: column; gap: 4px; }
.field-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #888); }
.field-input {
  padding: 8px 11px; border-radius: 7px; border: 1.5px solid var(--border-strong, rgba(31,30,26,.18));
  font-size: 13px; outline: none; background: var(--surface, #fff); color: var(--ink, #2c2b27); font-family: inherit;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.field-textarea { resize: vertical; }
.field-hint { margin: 8px 0 0; font-size: 12px; color: var(--ink-muted, #888); &--warn { color: var(--warning, #d83b01); } }
.form-error { font-size: 12px; color: #dc2626; margin: 0; }
.form-actions { display: flex; gap: 8px; margin-top: 16px; }

.empty-hint {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 60px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px;
  svg { opacity: .35; }
  &--compact { padding: 28px 20px; }
}

/* ── Lista ── */
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 14px; }
.quiz-card {
  border: 1px solid var(--border, rgba(31,30,26,.1)); border-radius: 14px; background: var(--card, #fff);
  display: flex; flex-direction: column; overflow: hidden;
  transition: box-shadow .15s;
  &:hover { box-shadow: var(--shadow-sm); }
}
.card-link-bar {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 10px 7px 14px; background: var(--accent-light, #deecf9); color: var(--system-navbar, #0f4c81);
  font-size: 11px; font-weight: 600;
}
.card-link-label { flex: 1; }
.copy-link-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 6px; border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 11px; font-weight: 600;
  &:hover { opacity: .88; }
}
.card-body { padding: 14px 16px 10px; flex: 1; display: flex; flex-direction: column; gap: 6px; cursor: pointer; }
.card-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.card-title { font-size: 14px; font-weight: 700; color: var(--ink, #2c2b27); }
.card-desc {
  font-size: 12px; color: var(--ink-muted, #888); line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.card-stats { display: flex; flex-wrap: wrap; gap: 4px; font-size: 12px; color: var(--ink-muted, #888); }
.vis-chip {
  flex-shrink: 0; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px;
  &--public { background: #dff6dd; color: #107c10; }
  &--private { background: rgba(31,30,26,.07); color: var(--ink-muted, #605e5c); }
}
.card-footer { display: flex; gap: 2px; padding: 8px 10px; border-top: 1px solid var(--border, rgba(31,30,26,.07)); }
.card-btn {
  padding: 5px 9px; border-radius: 6px; border: none; cursor: pointer;
  background: transparent; color: var(--ink-muted, #666); font-size: 11px; font-weight: 500;
  transition: background .12s;
  &:hover:not(:disabled) { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
  &:disabled { opacity: .4; cursor: not-allowed; }
  &--warn { margin-left: auto; &:hover:not(:disabled) { background: #fff7ed; color: #c2410c; } }
}

/* ── Detalhe ── */
.back-link {
  display: inline-flex; align-items: center; gap: 4px; margin-bottom: 12px;
  padding: 4px 8px 4px 4px; border: none; border-radius: 6px; background: transparent; cursor: pointer;
  font-size: 12px; font-weight: 600; color: var(--ink-muted, #605e5c);
  &:hover { background: rgba(31,30,26,.05); color: var(--ink, #2c2b27); }
}
.sub-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--border, rgba(31,30,26,.1)); margin-bottom: 18px; }
.sub-tab {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 14px; border: none; background: transparent; cursor: pointer;
  font-size: 13px; font-weight: 500; color: var(--ink-muted, #605e5c);
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  &:hover { color: var(--ink, #2c2b27); background: rgba(31,30,26,.03); }
  &.active { color: var(--system-navbar, #0f4c81); border-bottom-color: var(--system-navbar, #0f4c81); font-weight: 600; }
}
.sub-tab-count { font-size: 11px; color: var(--ink-faint, #a19f9d); font-weight: 600; }
.pane { min-width: 0; }

/* Editor + resumo lateral */
.detail-layout { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 24px; align-items: start; }
.detail-main { min-width: 0; }
.detail-aside {
  position: sticky; top: 0;
  display: flex; flex-direction: column; gap: 12px;
  max-height: calc(100vh - var(--navbar-h, 48px) - 48px); overflow-y: auto;
  scrollbar-width: thin;
}
.aside-card {
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1));
  border-radius: 12px; padding: 14px 16px;
}
.aside-hero { border-top: 3px solid var(--system-navbar, #0f4c81); }
.aside-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-faint, #a19f9d); }
.aside-title { margin: 4px 0 6px; font-size: 16px; font-weight: 800; color: var(--ink, #2c2b27); line-height: 1.3; word-break: break-word; }
.aside-desc {
  margin: 0 0 10px; font-size: 12px; line-height: 1.45; color: var(--ink-muted, #605e5c);
  display: -webkit-box; -webkit-line-clamp: 4; line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;
}
.aside-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.aside-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; background: var(--accent-light, #deecf9); color: var(--system-navbar, #0f4c81); }
.aside-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 10px; }
.aside-metric { display: flex; flex-direction: column; gap: 1px; }
.aside-metric-value { font-size: 22px; font-weight: 800; color: var(--ink, #2c2b27); line-height: 1.1; }
.aside-metric-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #888); }
.aside-completeness { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border, rgba(31,30,26,.08)); }
.aside-completeness-head { display: flex; justify-content: space-between; font-size: 12px; color: var(--ink-muted, #605e5c); margin-bottom: 6px; strong { color: var(--ink, #2c2b27); } }
.aside-bar { height: 6px; border-radius: 3px; background: var(--border, rgba(31,30,26,.1)); overflow: hidden; }
.aside-bar-fill { height: 100%; background: #107c10; transition: width .25s; }
.aside-section-title { font-size: 12px; font-weight: 700; color: var(--ink, #2c2b27); margin-bottom: 10px; }
.q-nav { display: flex; flex-wrap: wrap; gap: 5px; max-height: 220px; overflow-y: auto; }
.q-nav-dot {
  min-width: 30px; height: 26px; padding: 0 6px; border-radius: 6px; cursor: pointer;
  font-size: 11px; font-weight: 700; border: 1px solid transparent;
  transition: transform .1s;
  &:hover { transform: translateY(-1px); }
  &--ok { background: rgba(16,124,16,.1); color: #107c10; border-color: rgba(16,124,16,.25); }
  &--todo { background: rgba(216,59,1,.08); color: var(--warning, #d83b01); border-color: rgba(216,59,1,.3); }
}
.q-nav-legend {
  display: flex; gap: 12px; margin-top: 10px; font-size: 11px; color: var(--ink-muted, #888);
  span { display: inline-flex; align-items: center; gap: 5px; }
}
.lg { width: 8px; height: 8px; border-radius: 2px; &--ok { background: #107c10; } &--todo { background: var(--warning, #d83b01); } }
.aside-status { display: flex; flex-direction: column; gap: 8px; font-size: 12px; color: var(--ink-muted, #605e5c); }
.aside-status-row { display: flex; align-items: flex-start; gap: 8px; line-height: 1.4; &--action { margin-left: -8px; } }

.status-dot {
  flex-shrink: 0; width: 8px; height: 8px; margin-top: 4px; border-radius: 50%;
  &--saved { background: #107c10; }
  &--dirty { background: var(--system-navbar, #0f4c81); }
  &--saving { background: var(--system-navbar, #0f4c81); animation: dot-pulse 1s ease-in-out infinite; }
  &--pending { background: var(--warning, #d83b01); }
  &--error { background: #a4262c; }
}
@keyframes dot-pulse { 50% { opacity: .3; } }

.q-editor--flash { animation: q-flash 1.4s ease-out; }
@keyframes q-flash { 0%, 30% { box-shadow: 0 0 0 3px var(--accent-light, #deecf9), 0 0 0 4px var(--system-navbar, #0f4c81); } 100% { box-shadow: none; } }

.q-editor {
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1)); border-radius: 12px;
  padding: 14px 16px; margin-bottom: 12px;
}
.q-editor-head { display: flex; align-items: flex-start; gap: 10px; }
.q-num {
  flex-shrink: 0; width: 26px; height: 26px; margin-top: 4px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 12px; font-weight: 700;
}
.q-text { flex: 1; font-weight: 600; }
.q-tools { display: flex; gap: 2px; flex-shrink: 0; }
.icon-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px; border: none; cursor: pointer;
  background: transparent; color: var(--ink-muted, #605e5c);
  &:hover:not(:disabled) { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
  &:disabled { opacity: .3; cursor: not-allowed; }
  &--warn:hover:not(:disabled) { background: #fff7ed; color: #c2410c; }
}
.opt-list { display: flex; flex-direction: column; gap: 6px; margin: 12px 0 0 36px; }
.opt-row {
  display: flex; align-items: center; gap: 8px;
  padding: 3px 4px 3px 3px; border-radius: 8px; border: 1px solid transparent;
  &--correct { border-color: rgba(16,124,16,.35); background: rgba(16,124,16,.06); }
}
.opt-correct {
  position: relative; display: inline-flex; align-items: center; cursor: pointer;
  // rádio escondido fica preso ao rótulo (sem pai posicionado ele escapa do scroll e estica a página)
  input { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; pointer-events: none; }
}
.opt-letter {
  width: 26px; height: 26px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1.5px solid var(--border-strong, rgba(31,30,26,.25)); font-size: 11px; font-weight: 700; color: var(--ink-muted, #605e5c);
  transition: background .12s;
  .opt-row--correct & { background: #107c10; border-color: #107c10; color: #fff; }
  .opt-correct:hover & { border-color: #107c10; }
}
.opt-input { flex: 1; }
.opt-correct-tag { font-size: 11px; font-weight: 700; color: #107c10; }
.add-opt-btn {
  align-self: flex-start; margin-left: 34px; padding: 4px 8px; border: none; border-radius: 6px; background: transparent;
  cursor: pointer; font-size: 12px; font-weight: 600; color: var(--system-navbar, #0f4c81);
  &:hover { background: var(--accent-light, #deecf9); }
}
.add-q-btn {
  width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 12px; border-radius: 12px; cursor: pointer;
  border: 1.5px dashed var(--border-strong, rgba(31,30,26,.22)); background: transparent;
  font-size: 13px; font-weight: 600; color: var(--system-navbar, #0f4c81);
  &:hover { background: var(--accent-light, #deecf9); border-color: var(--system-navbar, #0f4c81); }
}
/* Barra encostada no rodapé: o .content do Dashboard tem 24px de padding (16px ≤900px),
   e o sticky respeita esse padding — o bottom/margin negativos compensam. */
.save-bar {
  position: sticky; bottom: -24px; z-index: 3;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin: 16px 0 -24px; padding: 12px 0;
  background: var(--bg, #f3f2f1);
  border-top: 1px solid var(--border, rgba(31,30,26,.1));
  box-shadow: 0 -6px 12px -8px rgba(0,0,0,.12);
}
.save-status { display: flex; align-items: flex-start; gap: 8px; min-width: 0; font-size: 12px; color: var(--ink-muted, #605e5c); }
.save-status-text { line-height: 1.4; &--err { color: #a4262c; } }
@media (max-width: 900px) {
  .save-bar { bottom: -16px; margin-bottom: -16px; }
}

.import-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 10px 14px; margin-bottom: 16px; border-radius: 10px;
  border: 1px dashed var(--border-strong, rgba(31,30,26,.22)); background: var(--surface, #fff);
}
.import-bar-text {
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
  strong { font-size: 13px; color: var(--ink, #2c2b27); }
  span { font-size: 12px; color: var(--ink-muted, #888); }
}
.import-bar-actions { display: flex; align-items: center; gap: 8px; .btn-secondary { display: inline-flex; align-items: center; gap: 6px; } }
.btn-link {
  padding: 6px 8px; border: none; border-radius: 6px; background: transparent; cursor: pointer;
  font-size: 12px; font-weight: 600; color: var(--system-navbar, #0f4c81);
  &:hover { background: var(--accent-light, #deecf9); }
}
.import-preview { border-left: 3px solid var(--system-navbar, #0f4c81); }
.import-summary { margin: 0 0 10px; font-size: 13px; color: var(--ink, #2c2b27); }
.import-err-count { color: var(--warning, #d83b01); font-weight: 600; }
.import-errors {
  margin: 0 0 12px; padding: 8px 12px 8px 28px; border-radius: 8px;
  background: rgba(216,59,1,.07); font-size: 12px; color: var(--ink, #2c2b27);
  li + li { margin-top: 3px; }
}
.import-sample { margin: 0 0 12px; padding-left: 20px; font-size: 12px; color: var(--ink, #2c2b27); li + li { margin-top: 4px; } }
.import-sample-answer { color: #107c10; font-weight: 600; }
.import-sample-more { list-style: none; color: var(--ink-muted, #888); }
.import-mode { display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px; }

.vis-options { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.vis-option {
  display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border-radius: 10px; cursor: pointer;
  border: 1.5px solid var(--border, rgba(31,30,26,.12));
  input { margin-top: 2px; accent-color: var(--system-navbar, #0f4c81); }
  &.active { border-color: var(--system-navbar, #0f4c81); background: var(--accent-light, #deecf9); }
}
.vis-option-title { font-size: 13px; font-weight: 700; color: var(--ink, #2c2b27); }
.vis-option-desc { margin-top: 2px; font-size: 12px; color: var(--ink-muted, #605e5c); line-height: 1.4; }
.link-field { display: flex; gap: 8px; margin-top: 12px; .field-input { flex: 1; font-family: Consolas, monospace; font-size: 12px; } }
.check-row {
  display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: var(--ink, #2c2b27);
  input { accent-color: var(--system-navbar, #0f4c81); width: 15px; height: 15px; }
}
.count-row { display: flex; align-items: center; gap: 10px; margin-top: 10px; .field-hint { margin: 0; } }
.count-input { width: 90px; }

/* ── Resultados ── */
.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
.stat {
  display: flex; flex-direction: column; gap: 2px; padding: 12px 14px; border-radius: 10px;
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1));
}
.stat-value { font-size: 20px; font-weight: 800; color: var(--ink, #2c2b27); }
.stat-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #888); text-transform: uppercase; letter-spacing: .03em; }
.table-wrap { overflow-x: auto; border: 1px solid var(--border, rgba(31,30,26,.1)); border-radius: 10px; background: var(--card, #fff); }
.att-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: left; padding: 9px 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: var(--ink-muted, #888); border-bottom: 1px solid var(--border, rgba(31,30,26,.1)); white-space: nowrap; }
  td { padding: 9px 12px; border-bottom: 1px solid var(--border, rgba(31,30,26,.06)); color: var(--ink, #2c2b27); white-space: nowrap; }
  .num { text-align: right; }
}
.att-row { cursor: pointer; &:hover td { background: rgba(31,30,26,.03); } &.open .row-caret { transform: rotate(90deg); } }
.row-caret { margin-right: 4px; color: var(--ink-faint, #a19f9d); transition: transform .12s; }
.src-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 10px; background: rgba(31,30,26,.06); color: var(--ink-muted, #605e5c); }
.pct-pill {
  font-size: 12px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
  &.tone-ok { background: rgba(16,124,16,.12); color: #107c10; }
  &.tone-mid { background: rgba(216,59,1,.12); color: #d83b01; }
  &.tone-low { background: rgba(164,38,44,.12); color: #a4262c; }
}
.att-detail-row td { white-space: normal; background: var(--bg, #f8f7f5); }
.detail-loading { font-size: 12px; color: var(--ink-muted, #888); }
.att-answers {
  margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 8px;
  li.ok::marker { color: #107c10; } li.err::marker { color: #a4262c; }
}
.att-q { font-size: 13px; font-weight: 600; }
.att-a { font-size: 12px; color: var(--ink-muted, #605e5c); margin-top: 2px; }
.att-mark { font-weight: 800; margin-right: 4px; .ok & { color: #107c10; } .err & { color: #a4262c; } }

/* ── Responder ── */
.take-wrap { max-width: 820px; }
.take-head { margin-bottom: 14px; }
.take-desc { white-space: pre-wrap; }

/* Tela estreita: o resumo sobe para cima do editor, em cartões lado a lado */
@media (max-width: 1180px) {
  .detail-layout { grid-template-columns: minmax(0, 1fr); }
  .detail-aside {
    order: -1; position: static; max-height: none; overflow: visible;
    display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
}
@media (max-width: 700px) {
  .vis-options, .stats-row { grid-template-columns: 1fr 1fr; }
  .opt-list { margin-left: 0; }
  .q-editor-head { flex-wrap: wrap; }
}
@media (max-width: 480px) {
  .vis-options { grid-template-columns: 1fr; }
  .cards-grid { grid-template-columns: 1fr; }
}

/* ── Categorias no editor ── */
.cats-toolbar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  margin: 4px 0 12px;
}
.cats-toolbar-text {
  display: flex; flex-direction: column; gap: 2px;
  strong { font-size: 14px; color: var(--ink, #2c2b27); }
  span { font-size: 12px; color: var(--ink-muted, #888); }
}
.cats-toolbar-actions { display: flex; gap: 2px; flex-wrap: wrap; }
.cat-block {
  margin-bottom: 14px; border-radius: 14px; overflow: hidden;
  border: 1px solid var(--border, rgba(31,30,26,.12)); background: var(--surface, #fff);
  border-left: 4px solid var(--system-navbar, #0f4c81);
}
.cat-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 12px; background: var(--card, #fff);
  .cat-block:not(.cat-block--collapsed) & { border-bottom: 1px solid var(--border, rgba(31,30,26,.08)); }
}
.cat-toggle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px; border: none; background: transparent; cursor: pointer;
  color: var(--ink-muted, #605e5c);
  &:hover { background: rgba(31,30,26,.06); }
}
.cat-chevron { transition: transform .15s; &.open { transform: rotate(90deg); } }
.cat-index {
  flex-shrink: 0; width: 26px; height: 26px; border-radius: 7px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--accent-light, #deecf9); color: var(--system-navbar, #0f4c81); font-size: 12px; font-weight: 800;
}
.cat-name-input {
  flex: 1; min-width: 160px; padding: 6px 8px; border-radius: 6px; border: 1.5px solid transparent;
  background: transparent; font-size: 15px; font-weight: 700; color: var(--ink, #2c2b27); font-family: inherit; outline: none;
  &:hover { border-color: var(--border, rgba(31,30,26,.12)); }
  &:focus { border-color: var(--system-navbar, #0f4c81); background: var(--surface, #fff); }
}
.cat-chip {
  flex-shrink: 0; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px;
  background: rgba(31,30,26,.06); color: var(--ink-muted, #605e5c);
  &--accent { background: var(--accent-light, #deecf9); color: var(--system-navbar, #0f4c81); }
  &--warn { background: rgba(216,59,1,.1); color: var(--warning, #d83b01); }
}
.cat-tools { display: flex; align-items: center; gap: 2px; margin-left: auto; }
.cat-import-btn {
  display: inline-flex; align-items: center; gap: 5px; margin-right: 4px;
  padding: 5px 10px; border-radius: 6px; cursor: pointer;
  border: 1px solid var(--border-strong, rgba(31,30,26,.18)); background: var(--surface, #fff);
  font-size: 12px; font-weight: 600; color: var(--ink, #2c2b27);
  &:hover { background: var(--accent-light, #deecf9); border-color: var(--system-navbar, #0f4c81); }
}
.cat-body { padding: 12px 14px 14px; background: var(--bg, #f8f7f5); }
.cat-settings {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-bottom: 12px; padding: 8px 12px; border-radius: 8px;
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.08));
  .count-input { width: 76px; padding: 5px 8px; }
}
.cat-settings-hint { margin: 0; }
.add-q-btn--small { padding: 9px; font-size: 12px; background: var(--card, #fff); }
.field-hint--flush { margin: 0; line-height: 1.5; }

/* ── Navegação lateral por categoria ── */
.q-nav-groups { display: flex; flex-direction: column; gap: 10px; max-height: 320px; overflow-y: auto; }
.q-nav-group .q-nav { max-height: none; }
.q-nav-cat {
  display: block; margin-bottom: 5px; padding: 0; border: none; background: transparent; cursor: pointer;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: var(--ink-muted, #605e5c);
  max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left;
  &:hover { color: var(--system-navbar, #0f4c81); }
}
.q-nav-empty { font-size: 11px; color: var(--ink-faint, #a19f9d); font-style: italic; }

/* ── Resultados: barra de visão + filtros ── */
.detail-layout--wide { grid-template-columns: minmax(0, 1fr); }
.res-toolbar { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
.seg {
  display: inline-flex; align-self: flex-start; padding: 3px; border-radius: 8px;
  background: rgba(31,30,26,.06);
}
.seg-btn {
  padding: 6px 14px; border-radius: 6px; border: none; background: transparent; cursor: pointer;
  font-size: 13px; font-weight: 600; color: var(--ink-muted, #605e5c);
  &.active { background: var(--card, #fff); color: var(--ink, #2c2b27); box-shadow: var(--shadow-sm); }
}
.res-filters { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
.filter { display: flex; flex-direction: column; gap: 3px; &--grow { flex: 1; min-width: 160px; } }
.filter-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #888); }
.filter-input { padding: 6px 9px; font-size: 12px; }
select.filter-input { max-width: 260px; }
.filter-clear { align-self: flex-end; }
.stat--warn .stat-value { color: var(--warning, #d83b01); }

/* ── Relatório resumido: roscas ── */
.tab-shell { --quiz-good: #107c10; --quiz-rest: rgba(31, 30, 26, .12); }
[data-theme="dark"] .tab-shell { --quiz-good: #6ccb5f; --quiz-rest: rgba(255, 255, 255, .14); }
.donut-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 12px; margin-bottom: 8px; }
.donut-card {
  margin: 0; padding: 14px 12px 12px; border-radius: 12px; text-align: center;
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1));
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  transition: box-shadow .15s;
  &:hover { box-shadow: var(--shadow-sm); }
  &--overall { border-top: 3px solid var(--system-navbar, #0f4c81); }
  figcaption { display: flex; flex-direction: column; gap: 2px; min-width: 0; max-width: 100%; }
}
.donut { width: 120px; height: 120px; }
.donut-value { stroke: var(--quiz-good); }
.donut-rest { stroke: var(--quiz-rest); }
.donut-pct { font-size: 17px; font-weight: 800; fill: var(--ink, #2c2b27); }
.donut-sub { font-size: 8px; font-weight: 600; fill: var(--ink-muted, #888); text-transform: uppercase; letter-spacing: .05em; }
.donut-name { font-size: 13px; font-weight: 700; color: var(--ink, #2c2b27); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.donut-meta { font-size: 11px; color: var(--ink-muted, #888); }
.donut-legend {
  display: flex; gap: 14px; margin: 0 0 16px; font-size: 11px; color: var(--ink-muted, #888);
  span { display: inline-flex; align-items: center; gap: 5px; }
}
.lg--good { background: var(--quiz-good); }
.lg--rest { background: var(--quiz-rest); }
.row-strong td { font-weight: 700; }

/* ── Resultados individuais: dispositivo ── */
.device-chip {
  padding: 2px 8px; border-radius: 10px; cursor: pointer; font-family: Consolas, monospace;
  border: 1px solid var(--border, rgba(31,30,26,.12)); background: transparent;
  font-size: 11px; font-weight: 600; color: var(--ink-muted, #605e5c);
  &:hover { border-color: var(--system-navbar, #0f4c81); color: var(--ink, #2c2b27); }
  &--shared { border-color: rgba(216,59,1,.4); background: rgba(216,59,1,.08); color: var(--warning, #d83b01); }
}
.muted { color: var(--ink-faint, #a19f9d); }
.shared-note {
  margin-bottom: 10px; padding: 7px 10px; border-radius: 6px;
  background: rgba(216,59,1,.08); color: var(--warning, #d83b01); font-size: 12px; font-weight: 600;
}
.att-cats { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.att-cat-chip {
  display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 12px;
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1)); font-size: 12px;
  strong.tone-ok { color: #107c10; } strong.tone-mid { color: #d83b01; } strong.tone-low { color: #a4262c; }
}
.att-group + .att-group { margin-top: 12px; }
.att-group-title { margin-bottom: 6px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: var(--ink-muted, #605e5c); }

/* ── Perguntas por tentativa (por categoria) ── */
.mode-select { padding: 5px 8px; font-size: 12px; width: auto; }
.cat-settings-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #605e5c); }
.cat-settings-result { margin-left: auto; font-size: 12px; font-weight: 600; color: var(--system-navbar, #0f4c81); }
.per-cat-intro { margin-bottom: 12px; }
.per-cat-table {
  .count-input { width: 72px; padding: 5px 8px; text-align: right; }
  tfoot td { border-top: 1px solid var(--border, rgba(31,30,26,.12)); border-bottom: none; }
}
.per-cat-name { max-width: 240px; overflow: hidden; text-overflow: ellipsis; font-weight: 600; }
.per-cat-warn {
  display: inline-flex; align-items: center; justify-content: center; margin-left: 6px;
  width: 16px; height: 16px; border-radius: 50%; background: rgba(216,59,1,.12); color: var(--warning, #d83b01);
  font-size: 11px; font-weight: 800; cursor: help;
}
.row-muted td { color: var(--ink-faint, #a19f9d); }
.per-cat-status { display: flex; align-items: flex-start; gap: 8px; margin-top: 10px; font-size: 12px; color: var(--ink-muted, #605e5c); }
</style>
